import {Injectable} from "@angular/core";
import {AssetId, BlobId, ProjectId, UserId} from "../types/ids";
import {Observable} from "rxjs";
import {AssetAndBlobApi} from '../api/rest/assetAndBlob.restapi';
import {GenericApiResponse} from '@webmate-web-core/shared/api/apibase.api';
import {take} from 'rxjs/operators';

@Injectable({
    providedIn: 'root'
})
export class AssetAndBlobService {

    /* We store the URLs to Assets here to save time. After all, many components will request the same over and over again,
     * for instance for Browsers. Not generating new links will be quicker. The actual "images" to which those URLs respond to
     * will be cached by the Browser... */
    private _assetSrcByIdCache: {[assetId: AssetId]: string} = {};
    private _blobSrcByIdCache: {[blobId: BlobId]: string} = {};

    /*
     _____ ____  _   _  _____ _______       _   _ _______ _____
    / ____/ __ \| \ | |/ ____|__   __|/\   | \ | |__   __/ ____|
   | |   | |  | |  \| | (___    | |  /  \  |  \| |  | | | (___
   | |   | |  | | . ` |\___ \   | | / /\ \ | . ` |  | |  \___ \
   | |___| |__| | |\  |____) |  | |/ ____ \| |\  |  | |  ____) |
    \_____\____/|_| \_|_____/   |_/_/    \_\_| \_|  |_| |_____/      */

    private constant_noProjectLogoUrl = '/assets/img/project-default-icon.svg';
    private constant_notFoundImageUrl = '/assets/img/questionmark.png';

    constructor( private assetAndBlobApi: AssetAndBlobApi) {}

    //   _____ ______ _______      _______ _____ ______             _____ _____
    //  / ____|  ____|  __ \ \    / /_   _/ ____|  ____|      /\   |  __ \_   _|
    // | (___ | |__  | |__) \ \  / /  | || |    | |__        /  \  | |__) || |
    //  \___ \|  __| |  _  / \ \/ /   | || |    |  __|      / /\ \ |  ___/ | |
    //  ____) | |____| | \ \  \  /   _| || |____| |____    / ____ \| |    _| |_
    // |_____/|______|_|  \_\  \/   |_____\_____|______|  /_/    \_\_|   |_____|


    obtainUrlToAssetData(assetId: AssetId | null): string {
        if (assetId === null) {
            return this.constant_notFoundImageUrl;
        } else {
            if (this._assetSrcByIdCache[assetId] === undefined){
                // No need for an api guard here, as the api does not actually make a call, but builds a URL
                const link = this.assetAndBlobApi.getUrlToAssetData(assetId);
                this._assetSrcByIdCache[assetId] = link;
            }
            return this._assetSrcByIdCache[assetId];
        }
    }

    obtainUrlToProjectLogo(blobId: BlobId): string {
        if (!!blobId) {
            return this.obtainUrlToBlobData(blobId);
        } else {
            return this.constant_noProjectLogoUrl;
        }
    }

    obtainUrlToBlobData(blobId: BlobId): string {
        if (blobId && this._blobSrcByIdCache[blobId] === undefined){
            // No need for an api guard here, as the api does not actually make a call, but builds a URL
            const link = this.assetAndBlobApi.getUrlToBlobData(blobId);
            this._blobSrcByIdCache[blobId] = link;
        }
        return this._blobSrcByIdCache[blobId];
    }

    obtainAssetDataAsJson(assetId: AssetId): Observable<any> {
        return this.assetAndBlobApi.getAssetDataAsJson(assetId);
    }

    putBlob(blob: Blob, projectId?: ProjectId): Observable<BlobId>{
        return this.assetAndBlobApi.putBlob(blob, projectId);
    }

    downloadBlob(blobId: BlobId, fileName: string) {
        this.assetAndBlobApi.getBlob(blobId).pipe(take(1)).subscribe(blob => {
            const a = document.createElement('a')
            const objectUrl = URL.createObjectURL(blob)
            a.href = objectUrl
            a.download = fileName;
            a.click();
            URL.revokeObjectURL(objectUrl);
        });
    }

    downloadAsset(assetId: AssetId, fileName: string) {
        this.assetAndBlobApi.downloadAssetAsBlob(assetId).pipe(take(1)).subscribe(blob => {
            const a = document.createElement('a')
            const objectUrl = URL.createObjectURL(blob)
            a.href = objectUrl
            a.download = fileName;
            a.click();
            URL.revokeObjectURL(objectUrl);
        });
    }

    createAsset(blobId: BlobId, creator: UserId, assetType: string, projectId?: ProjectId, name?: string, documentType?: string): Observable<AssetId>{
        return this.assetAndBlobApi.createAsset(blobId, creator, assetType, projectId, name, documentType);
    }

    deleteAsset(assetId: AssetId): Observable<GenericApiResponse> {
        return this.assetAndBlobApi.deleteAsset(assetId);
    }

    getBlob(blobId: BlobId): Observable<Blob> {
        return this.assetAndBlobApi.getBlob(blobId);
    }

}
